import { defineComponent as _defineComponent } from 'vue'
import { apolloClient, queries, mutations } from "@/apollo";
import { User, Release, StoredDdPage } from "@/CodegenTypes";
import { parseAra } from "@/helpers/ara/parseAra";
import { summariseStoredPage } from "@/helpers/ara/summariseStoredPage";
import { formatDollar } from "@/helpers/formatDollar";
import { errorMutationNotification } from "@/utils";
import AccountSelectModal from "@/components/modules/account/AccountSelectModal.vue";
import { getValidDayOptions } from "@/helpers/getValidDayOptions";
import { Day } from "@/types";
import { DAY_MAPPING } from "@/constants";
import { customMoment } from "@/main";
import { useRootStore } from "@/store/pinia";
import { useTranslation } from "@/store/translation";
import { useDialogStore } from "@/store/dialogs";
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useRoute } from "vue-router/composables";

type RouteQueryValue = string | (string | null)[] | null | undefined;


export default /*#__PURE__*/_defineComponent({
  __name: 'Upload',
  setup(__props) {

const route = useRoute();
const rootStore = useRootStore();
const vm = getCurrentInstance()?.proxy as any;
const { $t } = useTranslation();
const t = (text: string) => $t(text);

const showAccountSelectionModal = ref(false);
const releases = ref<null | Release[]>(null);
const isSubmitting = ref(false);
const selectedDayIndex = ref(0);
const resetFileUploadKey = ref(Symbol());

const selectedAccountId = computed(() => getRouteQueryValue(route.query.userId));
const releasesVariables = computed(() => {
  return {
    appMode: rootStore.appMode,
  };
});
const userVariables = computed(() => {
  return {
    userId: selectedAccountId.value,
  };
});
const storedPagesVariables = computed(() => {
  return {
    storedPageType: "upload",
    includeDownlines: true,
    userId: selectedAccountId.value,
  };
});
const {
  result: releasesResult,
  loading: releasesLoading,
  refetch: refetchReleases,
} = useQuery<{ releases: Release[] }>(queries.releases, releasesVariables, {
  fetchPolicy: "cache-and-network",
});
const {
  result: userResult,
  loading: userLoading,
  refetch: refetchUser,
} = useQuery<{ user: User }>(queries.userWithCreditSummary, userVariables, {
  enabled: computed(() => !!selectedAccountId.value),
  fetchPolicy: "cache-and-network",
});
const {
  result: storedPagesResult,
  loading: storedPagesLoading,
  refetch: refetchStoredPages,
} = useQuery<{ user: { storedPages: StoredDdPage[] } }>(
  queries.userStoredPages,
  storedPagesVariables,
  {
    enabled: computed(() => !!selectedAccountId.value),
    fetchPolicy: "cache-and-network",
  }
);

watch(
  releasesResult,
  (result) => {
    if (result?.releases) {
      releases.value = result.releases;
    }
  },
  { immediate: true }
);

const user = computed(() => userResult.value?.user ?? null);
const storedPages = computed(() => storedPagesResult.value?.user?.storedPages ?? null);
const apolloLoading = computed(
  () => releasesLoading.value || userLoading.value || storedPagesLoading.value
);
const openReleases = computed(() =>
  releases.value?.filter((release) => release.windowIsOpen)
);
const validDayOptions = computed(() => {
  if (!releases.value) {
    return [];
  }
  return getValidDayOptions(releases.value);
});
const dayOptions = computed(() => {
  const OPTIONS = [
    { label: "0=<Wed, Sat, Sun>", value: 0, defaultPriority: false },
    { label: "2=<Sat, Sun>", value: 2, defaultPriority: false },
    { label: "3=<Wed>", value: 3, defaultPriority: true },
    { label: "6=<Sat>", value: 6, defaultPriority: true },
    { label: "7=<Sun>", value: 7, defaultPriority: true },
  ];

  return OPTIONS.filter((o) => validDayOptions.value.includes(o.value));
});
const selectedDay = computed<Day>({
  get() {
    if (releases.value) {
      const selectedOption =
        dayOptions.value[selectedDayIndex.value] ?? dayOptions.value[0];
      return (selectedOption?.value ?? 0) as Day;
    }
    return 0;
  },
  set(val) {
    const index = dayOptions.value.findIndex((opt) => opt.value === val);
    selectedDayIndex.value = index === -1 ? 0 : index;
  },
});
const selectedReleases = computed(() => {
  const days = DAY_MAPPING[selectedDay.value];
  return releases.value?.filter((release) => days.includes(release.releaseDay));
});
const summarisedUploads = computed(() =>
  storedPages.value?.map((storedPage) => ({
    ...storedPage,
    summary: summariseStoredPage(storedPage),
    warnings: {
      noContainUserId:
        !!user.value &&
        !storedPage.uploadFileName?.toLowerCase().includes(user.value.userId),
      fileNameExists:
        storedPages.value &&
        storedPages.value.filter(
          (page) => page.uploadFileName === storedPage.uploadFileName
        ).length > 1,
    },
  }))
);

onMounted(async () => {
  const priorityIndex = dayOptions.value.findIndex((opt) => opt.defaultPriority);
  selectedDayIndex.value = priorityIndex === -1 ? 0 : priorityIndex;

  const releaseResult = await apolloClient.query<{ releases: Release[] }>({
    query: queries.releases,
    variables: releasesVariables.value,
  });
  releases.value = releaseResult.data.releases;
});

function getRouteQueryValue(query: RouteQueryValue) {
  return Array.isArray(query) ? query[0] : query;
}

function handleFileUpload({ files }: { files: File[] }) {
  const file = files[0];
  const reader = new FileReader();
  reader.addEventListener("load", async (event) => {
    const line = event.target?.result;

    if (!selectedReleases.value || !selectedAccountId.value) {
      throw new Error("Missing variables");
    }

    try {
      const parsedAras = parseAra(line as string, selectedDay.value);
      if (parsedAras.length === 0) {
        throw new Error("No valid entries found");
      }
      const firstParsedAra = parsedAras[0];
      await apolloClient.mutate({
        mutation: mutations.saveDdEntry,
        variables: {
          page: {
            page: firstParsedAra.page,
            entries: parsedAras.map((parsedAra) => ({
              day: selectedDay.value,
              number: parsedAra.number,
              big: parsedAra.big,
              small: parsedAra.small,
              mode: parsedAra.mode,
            })),
            uploadMeta: {
              fileName: file.name,
              fileSize: file.size,
            },
            userId: selectedAccountId.value,
            storedPageType: "upload",
          },
        },
      });
      resetFileUploadKey.value = Symbol();
      void reloadAll();

      useDialogStore().addDialog({
        text: "File uploaded successfully! You can see your upload below. Click on green load button to confirm.",
      });
    } catch (err) {
      errorMutationNotification(err);
    }
  });
  if (file) {
    reader.readAsText(file);
  }
}

function formatDatetime(date: Date) {
  return customMoment(date).format("DD MMM YYYY hh:mm:ss A");
}

async function submitFile(index: number) {
  const targetUpload = storedPages.value?.[index];
  if (!targetUpload) {
    return;
  }
  try {
    isSubmitting.value = true;

    await apolloClient.mutate({
      mutation: mutations.insertDdEntry,
      variables: {
        page: {
          page: targetUpload.name,
          userId: selectedAccountId.value,
          entries: targetUpload.entries.map((entry) => {
            return {
              number: entry.number,
              day: entry.day,
              big: entry.big,
              small: entry.small,
              mode: entry.mode,
            };
          }),
          entrySource: "upload",
        },
      },
    });
    void reloadAll();
    useDialogStore().addDialog({
      text: "Upload successfully loaded into bet list.",
    });
    await handleDelete(targetUpload.id, false);
  } catch (err) {
    errorMutationNotification(err);
  } finally {
    isSubmitting.value = false;
  }
}

async function handleDelete(pageId: string, doPrompt = true) {
  if (!selectedAccountId.value) {
    vm.$toast.add({
      severity: "error",
      summary: "Error",
      detail: "No account selected",
      life: 3000,
    });
    return;
  }

  const performDelete = async () => {
    try {
      isSubmitting.value = true;
      await apolloClient.mutate({
        mutation: mutations.deleteSavedDdPage,
        variables: {
          id: pageId,
        },
      });
      void reloadAll();
      if (doPrompt) {
        vm.$toast.add({
          severity: "success",
          summary: "Success",
          detail: "Upload deleted.",
          life: 3000,
        });
      }
    } catch (err) {
      errorMutationNotification(err);
    } finally {
      isSubmitting.value = false;
    }
  };

  if (doPrompt) {
    vm.$confirm.require({
      header: "Confirm?",
      message: "Are you sure to delete the upload?",
      icon: "pi pi-exclamation-triangle",
      accept: performDelete,
      reject: () => {
        // Optional: Handle rejection
      },
    });
  } else {
    await performDelete();
  }
}

async function reloadAll() {
  await Promise.all([
    refetchReleases(releasesVariables.value),
    selectedAccountId.value ? refetchUser(userVariables.value) : Promise.resolve(),
    selectedAccountId.value
      ? refetchStoredPages(storedPagesVariables.value)
      : Promise.resolve(),
  ]);
}

return { __sfc: true,route, rootStore, vm, $t, t, showAccountSelectionModal, releases, isSubmitting, selectedDayIndex, resetFileUploadKey, selectedAccountId, releasesVariables, userVariables, storedPagesVariables, releasesResult, releasesLoading, refetchReleases, userResult, userLoading, refetchUser, storedPagesResult, storedPagesLoading, refetchStoredPages, user, storedPages, apolloLoading, openReleases, validDayOptions, dayOptions, selectedDay, selectedReleases, summarisedUploads, getRouteQueryValue, handleFileUpload, formatDatetime, submitFile, handleDelete, reloadAll, formatDollar, AccountSelectModal }
}

})